import { getAuth } from 'firebase/auth'
import { firebaseApp } from './firebase'
import { resolveTenantSlug } from './tenant'

/**
 * Thin wrapper over fetch that:
 *   1. Attaches the current user's ID token as a Bearer Authorization header
 *   2. Resolves JSON responses
 *   3. Throws on non-2xx with the server's error payload preserved
 *
 * Usage:
 *   const res = await api<SessionStartResponse>('/v1/sessions/start', {
 *     method: 'POST',
 *     body: { quizId, passcode, clientTimeMs: Date.now() },
 *   })
 */

const BASE = import.meta.env.VITE_API_BASE_URL

export class ApiError extends Error {
  constructor(
    public status: number,
    public payload: unknown,
    message: string,
    /**
     * Whether the server actually answered. False for the wrapper's own
     * synthetic errors — the request deadline firing, or there being no
     * signed-in user to attach a token for. Those carry an HTTP-looking
     * status but say nothing about the request's fate, so a caller deciding
     * whether a mutating request took effect must branch on this, not on
     * `status`.
     */
    public receivedResponse = true,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
  /** Total request deadline; bulk operations may need longer than 20 seconds. */
  timeoutMs?: number
  /** Skip the auth header — used only by the login flow itself. */
  unauthenticated?: boolean
  /**
   * Let the request outlive the page. Integrity events are fired as the exam
   * page is being backgrounded, so without this the browser cancels the POST
   * mid-flight and the violation is never counted.
   */
  keepalive?: boolean
}

export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const headers: Record<string, string> = {}

  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json'
  }

  // Tenant header goes on every call — the unauthenticated handoff exchange
  // needs it too, and the server ignores it where irrelevant.
  const tenantSlug = resolveTenantSlug()
  if (tenantSlug) {
    headers['X-Tenant-Slug'] = tenantSlug
  }

  const controller = new AbortController()
  const abort = () => controller.abort(opts.signal?.reason)
  if (opts.signal?.aborted) abort()
  else opts.signal?.addEventListener('abort', abort, { once: true })

  let timer: ReturnType<typeof setTimeout>
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      // receivedResponse: false — the request may still have reached the
      // server and committed; the deadline only means we stopped waiting.
      reject(new ApiError(408, null, 'Request timed out. Check your connection and try again.', false))
      controller.abort()
    }, opts.timeoutMs ?? 20_000)
  })

  try {
    if (!opts.unauthenticated) {
      const auth = getAuth(firebaseApp)
      const user = auth.currentUser
      if (!user) throw new ApiError(401, null, 'not signed in', false)
      const token = await Promise.race([user.getIdToken(/* forceRefresh */ false), timeout])
      headers.Authorization = `Bearer ${token}`
    }

    const res = await Promise.race([fetch(`${BASE}${path}`, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: controller.signal,
      ...(opts.keepalive ? { keepalive: true } : {}),
    }), timeout])

    // 204 No Content
    if (res.status === 204) return undefined as T

    const data: unknown = await Promise.race([res.json().catch(() => ({})), timeout])
    if (!res.ok) {
      // A password session minted while an access-code lock was active is
      // dead at the API — end it so the UI cannot sit in a broken signed-in
      // state. onIdTokenChanged in the auth store clears the session state.
      if (res.status === 401 && (data as { error?: string })?.error === 'AccessCodeLocked') {
        void getAuth(firebaseApp).signOut()
      }
      const msg =
        (data as { message?: string })?.message ?? `HTTP ${res.status}`
      throw new ApiError(res.status, data, msg)
    }
    return data as T
  } finally {
    clearTimeout(timer!)
    opts.signal?.removeEventListener('abort', abort)
  }
}

/** Fetch an authenticated binary response, such as a student receipt PDF. */
export async function apiBlob(path: string, opts: Omit<ApiOptions, 'timeoutMs'> = {}): Promise<Blob> {
  const headers: Record<string, string> = {}
  const tenantSlug = resolveTenantSlug()
  if (tenantSlug) headers['X-Tenant-Slug'] = tenantSlug
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json'

  if (!opts.unauthenticated) {
    const auth = getAuth(firebaseApp)
    const user = auth.currentUser
    if (!user) throw new ApiError(401, null, 'not signed in', false)
    headers.Authorization = `Bearer ${await user.getIdToken(/* forceRefresh */ false)}`
  }

  const res = await fetch(`${BASE}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  })
  if (!res.ok) {
    const data: unknown = await res.json().catch(() => ({}))
    const message = (data as { message?: string })?.message ?? `HTTP ${res.status}`
    throw new ApiError(res.status, data, message)
  }
  return res.blob()
}
